import { parseQuat, parseVec3 } from "../parserUtils";
import { parsePassthroughAttributes } from "./attributeUtils";
import type { MeshUUID } from "../../types/scene";
import type { ParsedHFieldsMap, ParsedMeshesMap, ParsedTexture, ParsedTexturesMap } from "./types";

const parseScaleAttr = (value: string | null): number[] | null => {
  if (!value) return null;
  const parts = value
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((component) => Number.isFinite(component));
  if (parts.length === 0) return null;
  return parts;
};

const parseNumbers = (value: string | null): number[] => {
  if (!value) return [];
  return value
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((component) => Number.isFinite(component));
};

const extractMeshUuid = (value: string): string | null => {
  const s = String(value || "").trim();
  if (!s) return null;
  const m = s.match(/^mesh_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i);
  return m && m[1] ? m[1] : null;
};

const resolveScaleFromClass = (
  className: string | null,
  classScaleMap: Record<string, number[]>,
  classParentMap: Record<string, string>
): number[] | null => {
  if (!className) return null;
  let current: string | null = className;
  while (current) {
    const classScale = classScaleMap?.[current];
    if (Array.isArray(classScale) && classScale.length > 0) {
      return classScale.slice();
    }
    current = classParentMap?.[current] ?? null;
  }
  return null;
};

export const parseMeshDefinitions = (args: {
  xmlDoc: Document;
  classScaleMap: Record<string, number[]>;
  classParentMap: Record<string, string>;
  existingMeshes?: ParsedMeshesMap;
}): ParsedMeshesMap => {
  const meshDefinitions: ParsedMeshesMap = { ...(args.existingMeshes || {}) };
  args.xmlDoc.querySelectorAll("asset > mesh").forEach((meshElem: Element) => {
    const file = meshElem.getAttribute("file") || meshElem.getAttribute("obj");
    let meshName = meshElem.getAttribute("name");

    if (!meshName && file) {
      meshName = file.replace(/^.*[/\\]/, "").replace(/\.(obj|stl)$/i, "");
    }

    if (!meshName || !file) return;

    const explicitScale = parseScaleAttr(meshElem.getAttribute("scale"));
    const resolvedScale =
      explicitScale ??
      resolveScaleFromClass(
        meshElem.getAttribute("class"),
        args.classScaleMap,
        args.classParentMap
      ) ??
      null;

    const extractedUuid = extractMeshUuid(meshName);
    const definition = meshDefinitions[meshName] || { name: meshName };
    if (extractedUuid) {
      definition.uuid = extractedUuid as MeshUUID;
    }

    if (resolvedScale && resolvedScale.length === 3) {
      definition.scale = [
        Number(resolvedScale[0]),
        Number(resolvedScale[1]),
        Number(resolvedScale[2]),
      ];
    }

    const inertiaAttr = meshElem.getAttribute("inertia");
    if (
      inertiaAttr === "convex" ||
      inertiaAttr === "exact" ||
      inertiaAttr === "legacy" ||
      inertiaAttr === "shell"
    ) {
      definition.inertiaComputation = inertiaAttr;
    }

    const refPosAttr = meshElem.getAttribute("refpos");
    if (refPosAttr) {
      const parsed = parseVec3(refPosAttr);
      if (parsed) definition.referencePosition = parsed;
    }

    const refQuatAttr = meshElem.getAttribute("refquat");
    if (refQuatAttr) {
      const parsedQuat = parseQuat(refQuatAttr);
      if (parsedQuat) definition.referenceOrientation = parsedQuat;
    }

    definition.url = file;

    meshDefinitions[meshName] = definition;
  });
  return meshDefinitions;
};

export const parseHFields = (xmlDoc: Document): ParsedHFieldsMap => {
  const hfieldDefinitions: ParsedHFieldsMap = {};
  xmlDoc.querySelectorAll("asset > hfield").forEach((hfieldElem: Element) => {
    let name = hfieldElem.getAttribute("name") || "";
    const file = hfieldElem.getAttribute("file") || undefined;
    if (!name && file) {
      name = file.replace(/^.*[\\/]/, "").replace(/\.[^./\\]+$/, "");
    }
    if (!name) return;

    const sizeValues = parseNumbers(hfieldElem.getAttribute("size"));
    const size: [number, number, number, number] = [
      sizeValues[0] ?? 1,
      sizeValues[1] ?? 1,
      sizeValues[2] ?? 0.1,
      sizeValues[3] ?? 0.01,
    ];
    const data = parseNumbers(hfieldElem.getAttribute("elevation"));
    const nrow = Number(hfieldElem.getAttribute("nrow"));
    const ncol = Number(hfieldElem.getAttribute("ncol"));

    hfieldDefinitions[name] = {
      name,
      file,
      contentType: hfieldElem.getAttribute("content_type") || undefined,
      size,
      nrow: Number.isFinite(nrow) ? nrow : undefined,
      ncol: Number.isFinite(ncol) ? ncol : undefined,
      data: data.length ? data : undefined,
    };
  });

  return hfieldDefinitions;
};

export const parseTextures = (xmlDoc: Document): ParsedTexturesMap => {
  const parsedTextures: ParsedTexturesMap = {};
  xmlDoc.querySelectorAll("asset > texture").forEach((tex: Element) => {
    const name = tex.getAttribute("name");
    if (!name) return;
    const file = tex.getAttribute("file") || undefined;
    const typeAttr = (tex.getAttribute("type") || "2d").toLowerCase();
    const widthAttr = tex.getAttribute("width");
    const heightAttr = tex.getAttribute("height");
    const nchannelAttr = tex.getAttribute("nchannel");

    const width = widthAttr != null ? Number(widthAttr) : undefined;
    const height = heightAttr != null ? Number(heightAttr) : undefined;
    const nchannel = nchannelAttr != null ? Number(nchannelAttr) : undefined;

    const kind: ParsedTexture["kind"] = typeAttr || "2d";
    const attrs = parsePassthroughAttributes(tex, ["name"]);

    parsedTextures[name] = {
      file,
      kind,
      width,
      height,
      nchannel,
      attrs,
    };
  });
  return parsedTextures;
};
